import {
    cssX,
    IDropdownColumn,
    markMatch,
    SearchBox,
    SearchBoxLoading,
    SearchBoxNoResults,
    TextInput,
    usePlatformFeatureFlag,
    useUuid
} from "@datassential/platform-ui-lib";
import { useCallback, useEffect, useMemo, useState, ClipboardEvent } from "react";
import { useSearchResults } from "@apiHooks/useSearchResults";
import { iSearchResultsSection, ItemTypeEnum, SearchResults } from "@models/SearchResults";
import { useSelector } from "react-redux";
import { currentCountrySelector } from "@selectors/countries.selector";
import { ID, ReportFileExtension } from "@models";
import { Link, useNavigate } from "react-router-dom";
import { Routes } from "@routes";
import { ExportTypeEnum } from "@models/ExportTypeEnum";
import http from "@core/http";
import apiConfig from "@apiConfig";
import cn from "classnames";
import { AiOutlineSearch } from 'react-icons/ai';
import { parseWordsFromString } from "@core/helpers";
import { MULTILINE_CHARS } from "@components/SearchField/SearchField";
import { FEATURE_AI_SEARCH } from "@constants/general";
import ToolSearch from "./ToolSearch";
import { toast } from "react-toastify";

const MIN_RESULTS_COUNT = 3;

const HeaderSearch = () => {
    const navigate = useNavigate();
    const id = useUuid();
    const [query, setQuery] = useState('');
    const [selectedItemTypeId, setSelectedItemTypeId] = useState<ID | null>(null);
    const [isToolSearchOpened, setIsToolSearchOpened] = useState(false);
    const currentCountry = useSelector(currentCountrySelector);
    const isAiSearchEnabled = usePlatformFeatureFlag(FEATURE_AI_SEARCH);

    const openToolSearch = useCallback(() => setIsToolSearchOpened(true), []);
    const closeToolSearch = useCallback(() => setIsToolSearchOpened(false), []);

    const searchResults = useSearchResults(currentCountry, query);
    const { isLoading, data } = query.trim() ? searchResults : { isLoading: false, data: new SearchResults() };

    const downloadSummaryReport = useCallback(
        (reportId: ID, eTypeOfExport: ExportTypeEnum) => {
            toast.info('Downloading...');
            http.downloadFileXHRFromUrl(
                'GET',
                apiConfig.get('export report', { reportId, eTypeOfExport, countryCode: currentCountry })
            ).then(() => {
                toast.success('Download was successful');
            });
        },
        [currentCountry]
    )

    const downloadCategoryReport = useCallback(
        (reportId: ID, eTypeOfExport: ReportFileExtension) => {
            toast.info('Downloading...');
            http.downloadFileXHRFromUrl(
                'GET',
                apiConfig.get('category report', { reportId, eTypeOfExport, countryCode: currentCountry })
            ).then(() => {
                toast.success('Download was successful');
            });
        },
        [currentCountry]
    );

    const mapResultsSection = useCallback(
        (section: iSearchResultsSection): IDropdownColumn  => {
            const isOpened = selectedItemTypeId === section.id;

            return {
                id: section.id,
                title: section.title.toUpperCase(),
                items: section.items.slice(0, isOpened ? Infinity : MIN_RESULTS_COUNT).map((item, index) => {
                    const props = {
                        className: cn(
                            cssX('Dropdown__item'),
                            "PlatformHeader__search-item",
                        ),
                        children: (<>
                            <span
                                className="PlatformHeader__search-item-title"
                                data-testid="textHeaderSearchResultTitle"
                                dangerouslySetInnerHTML={{
                                    __html: markMatch(item.name, query.trim()),
                                }}
                            />
                            &nbsp;
                            <span
                                className="PlatformHeader__search-item-sub-title"
                                data-testid="textHeaderSearchResultSubTitle"
                            >
                                {SearchResults.getItemSubTitle(item, index, section.id)}
                            </span>
                        </>),
                    };

                    return {
                        id: item.id,
                        content: (<>
                            {section.id === ItemTypeEnum.FoodProfile && (
                                <div {...props}/>
                            )}
                            {section.id !== ItemTypeEnum.FoodProfile && (
                                <Link
                                    to={SearchResults.getItemLink(item, section.id)}
                                    {...props}
                                />
                            )}
                            {section.id === ItemTypeEnum.FoodProfile && (
                                <div className={cssX('Dropdown__item-actions')}>
                                    <Link
                                        to={SearchResults.getItemLink(item, section.id)}
                                        className={cn(
                                            cssX('Dropdown__item-action'),
                                            'PlatformHeader__search-action',
                                            'is-medium',
                                        )}
                                        data-testid="buttonHeaderSearchResultFoodTrend"
                                    >
                                        Trend
                                    </Link>
                                    {item.isFoodProfile && (
                                        <Link
                                            className={cn(
                                                cssX('Dropdown__item-action'),
                                                'PlatformHeader__search-action',
                                                'is-medium',
                                            )}
                                            data-testid="buttonHeaderSearchResultFoodProfile"
                                            to={`${Routes.foodProfile}?id=${item.id}&name=${encodeURIComponent(item.name)}`}
                                        >
                                            Profile
                                        </Link>
                                    )}
                                </div>
                            )}
                            {section.id === ItemTypeEnum.Report && (
                                <div className={cssX('Dropdown__item-actions')}>
                                    <div
                                        className={cn(
                                            cssX('Dropdown__item-action'),
                                            'PlatformHeader__search-action',
                                            'is-small',
                                        )}
                                        data-testid="buttonHeaderSearchReportPdf"
                                        onClick={() => downloadSummaryReport(item.id, ExportTypeEnum.Pdf)}
                                    >
                                        PDF
                                    </div>
                                    <div
                                        className={cn(
                                            cssX('Dropdown__item-action'),
                                            'PlatformHeader__search-action',
                                            'is-small',
                                        )}
                                        data-testid="buttonHeaderSearchReportPpt"
                                        onClick={() => downloadSummaryReport(item.id, ExportTypeEnum.Ppt)}
                                    >
                                        PPT
                                    </div>
                                </div>
                            )}
                            {section.id === ItemTypeEnum.CategoryReport && (
                                <div className={cssX('Dropdown__item-actions')}>
                                    <div
                                        className={cn(
                                            cssX('Dropdown__item-action'),
                                            'PlatformHeader__search-action',
                                            'is-small',
                                        )}
                                        data-testid="buttonHeaderSearchCategoryReportPdf"
                                        onClick={() => downloadCategoryReport(item.id, ReportFileExtension.Pdf)}
                                    >
                                        PDF
                                    </div>
                                    <div
                                        className={cn(
                                            cssX('Dropdown__item-action'),
                                            'PlatformHeader__search-action',
                                            'is-small',
                                        )}
                                        data-testid="buttonHeaderSearchCategoryReportPpt"
                                        onClick={() => downloadCategoryReport(item.id, ReportFileExtension.Ppt)}
                                    >
                                        PPT
                                    </div>
                                </div>
                            )}
                        </>),
                    }
                }),
                content: section.items.length > MIN_RESULTS_COUNT && (
                    <div
                        className={cssX("Dropdown__more-results")}
                        onClick={() => setSelectedItemTypeId(isOpened ? null : section.id)}
                        data-testid="buttonHeaderSearchCategoryMoreResults"
                    >
                        {isOpened ? 'HIDE' : 'MORE'}
                    </div>
                ),
            }
        },
        [selectedItemTypeId, query, downloadSummaryReport, downloadCategoryReport]
    );

    useEffect(() => {
        setSelectedItemTypeId(null);
    }, [setQuery]);

    const searchAutocompleteGroups = useMemo(
        (): IDropdownColumn[] => {
            return data.data
                .filter(i => i.items.length > 0)
                .filter(i => {
                    if (selectedItemTypeId) {
                        return i.id === selectedItemTypeId;
                    }

                    return true;
                })
                .map(mapResultsSection);
        },
        [data.data, mapResultsSection, selectedItemTypeId]
    );

    const children = useMemo(
        () => {
            if (isLoading) return (
                <SearchBoxLoading/>
            );

            if (!isLoading && searchAutocompleteGroups?.length === 0) return (
                <SearchBoxNoResults/>
            );

            return null;
        },
        [isLoading, searchAutocompleteGroups]
    );

    const hasData = useMemo(
        () => !isLoading && searchAutocompleteGroups.length > 0,
        [isLoading, searchAutocompleteGroups]
    );

    return (
        <>
            {isAiSearchEnabled ? (
                <div
                    className={cssX("SearchBox")}
                    data-testid="selectHeaderSearch"
                    style={{ cursor: "pointer" }}
                    onClick={openToolSearch}
                >
                    <TextInput
                        value=""
                        setValue={() => null}
                        placeholder="Search in Menu Trends"
                        style={{ pointerEvents: "none"}}
                        className={cssX("SearchBox__input")}
                    />
                    {/* @ts-ignore */}
                    <AiOutlineSearch className={cssX("SearchBox__icon")}/>
                </div>
            ) : (
                <SearchBox
                    testId={"selectHeaderSearch"}
                    tooltipId={id}
                    inputProps={{
                        value: query,
                        setValue: (value: string) => {
                            setQuery(value);
                            setSelectedItemTypeId(null);
                        },
                        onPaste: (e: ClipboardEvent) => {
                            const buffer = e.clipboardData.getData('text');
                            if (MULTILINE_CHARS.some(char => buffer.includes(char))) {
                                const words = parseWordsFromString(buffer);
                                setTimeout(
                                    () => {
                                        setQuery(words.join(', '));
                                    },
                                    0
                                );
                            }
                        },
                        onSubmit: () => {
                            navigate(`${Routes.byo}?words=${encodeURIComponent(query)}`);
                            setQuery('');
                        },
                        placeholder: 'Search in Menu Trends',
                        testId: 'inputHeaderSearch',
                    }}
                    dropdownProps={{
                        isOpened: query.trim() !== '',
                        groups: hasData ? searchAutocompleteGroups : undefined,
                        id,
                        testId: "selectHeaderSearchDropdown",
                        isColTitlesSticky: true,
                        style: {
                            maxHeight: 300,
                            overflowX: 'hidden',
                        },
                        children: query !== '' ? children : null,
                    }}
                />
            )}
            <ToolSearch isOpen={isToolSearchOpened} close={closeToolSearch}/>
        </>
    );
}

export default HeaderSearch;